<template>
  <!-- mobile design -->
  <v-container
    v-if="$vuetify.breakpoint.mdAndDown"
    class="mt-5"
  >
    <v-dialog
      v-model="searchDialogMobile"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
    >
      <template v-slot:activator="{ on, attrs }">
        <div
          v-bind="attrs"
          v-on="on"
          class="d-flex white rounded-lg pa-3"
        >
          <h2 class="flex-grow-1 text-subtitle-1">Search</h2>
          <v-icon>mdi-menu-right</v-icon>
        </div>
      </template>
      <v-card color="background">
        <v-toolbar color="primary">
          <v-btn
            icon
            @click="searchDialogMobile = false"
          >
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <v-toolbar-title>Search</v-toolbar-title>
        </v-toolbar>
        <div
          v-if="isAllowedSearchBy('SEARCH_BY_CODE_OR_GRADE_NAME')"
          class="d-flex rounded-lg pa-3 mx-6 my-5"
          :class="[searchBy === 'SEARCH_BY_CODE_OR_GRADE_NAME' ? 'primary' : 'white']"
          @click="setSearchBy('SEARCH_BY_CODE_OR_GRADE_NAME')"
        >
          <div class="flex-grow-1 text-subtitle-1">{{searchByText('SEARCH_BY_CODE_OR_GRADE_NAME')}}</div>
          <v-icon>mdi-menu-down</v-icon>
        </div>
        <div
          v-if="searchBy === 'SEARCH_BY_CODE_OR_GRADE_NAME'"
          class="d-flex mx-6 my-5 align-center"
        >
          <v-combobox
            class=""
            :value="selectedCode"
            @input="val => {searchDialogMobile = false; fetchByCode(val)}"
            :items="suggestionConverterCodes"
            :loading="isConvertersCodesLoading"
            :search-input.sync="searchCatalyticConvertersInput"
            solo
            :error-messages="suggestionConverterCodesError"
            :hide-details="!suggestionConverterCodesError"
            hide-no-data
            hide-selected
            placeholder="Type to Search"
            prepend-inner-icon="mdi-magnify"
            dense
          ></v-combobox>
          <v-btn class="ms-2" small color="primary">Search</v-btn>
        </div>
        <div
          v-if="isAllowedSearchBy('FILTER_BY_MANUFACTURER')"
          class="d-flex rounded-lg pa-3 mx-6 my-5"
          :class="[searchBy === 'FILTER_BY_MANUFACTURER' ? 'primary' : 'white']"
          @click="setSearchBy('FILTER_BY_MANUFACTURER')"
        >
          <div class="flex-grow-1 text-subtitle-1">{{searchByText('FILTER_BY_MANUFACTURER')}}</div>
          <v-icon>mdi-menu-down</v-icon>
        </div>
        <v-autocomplete
          v-if="searchBy === 'FILTER_BY_MANUFACTURER'"
          class="mx-6 my-5"
          :value="selectedManufacturer"
          @input="val => {searchDialogMobile = false; fetchByManufacturer(val)}"
          :items="allManufacturers"
          :loading="isManufacturersLoading"
          solo
          :error-messages="manufacturersError"
          :hide-details="!manufacturersError"
          hide-no-data
          placeholder="Type to Search"
          prepend-inner-icon="mdi-filter-variant"
          dense
        ></v-autocomplete>
        <div
          v-if="isAllowedSearchBy('FILTER_BY_VEHICLE')"
          class="d-flex rounded-lg pa-3 mx-6 my-5"
          :class="[searchBy === 'FILTER_BY_VEHICLE' ? 'primary' : 'white']"
          @click="setSearchBy('FILTER_BY_VEHICLE')"
        >
          <div class="flex-grow-1 text-subtitle-1">{{searchByText('FILTER_BY_VEHICLE')}}</div>
          <v-icon>mdi-menu-down</v-icon>
        </div>
        <template v-if="searchBy === 'FILTER_BY_VEHICLE'">
          <v-autocomplete
            class="mx-6 my-5"
            :value="selectedYear"
            @input="selectYear"
            :items="allYears"
            :loading="isYearsLoading"
            solo
            :error-messages="yearsError"
            :hide-details="!yearsError"
            hide-no-data
            label="Year"
            placeholder="Type to Search"
            prepend-inner-icon="mdi-filter-variant"
            dense
          ></v-autocomplete>
          <v-autocomplete
            class="mx-6 my-5"
            :value="selectedMake"
            @input="selectMake"
            :items="makes"
            :loading="isMakesLoading"
            :disabled="isMakesLoading || !selectedYear"
            solo
            :error-messages="makesError"
            :hide-details="!makesError"
            hide-no-data
            label="Make"
            placeholder="Type to Search"
            prepend-inner-icon="mdi-filter-variant"
            dense
          ></v-autocomplete>
          <v-autocomplete
            class="mx-6 my-5"
            :value="selectedModel"
            @input="selectModel"
            :items="models"
            :loading="isModelsLoading"
            :disabled="isModelsLoading || !(selectedYear && selectedMake)"
            solo
            :error-messages="modelsError"
            :hide-details="!modelsError"
            hide-no-data
            label="Model"
            placeholder="Type to Search"
            prepend-inner-icon="mdi-filter-variant"
            dense
          ></v-autocomplete>
          <v-autocomplete
            class="mx-6 my-5"
            :value="selectedEngineSize"
            @input="val => {searchDialogMobile = false; selectEngineSize(val)}"
            :items="engineSizes"
            :loading="isEngineSizesLoading"
            :disabled="isEngineSizesLoading || !(selectedYear && selectedMake && selectedModel) "
            solo
            :error-messages="engineSizesError"
            :hide-details="!engineSizesError"
            hide-no-data
            label="Engine Size"
            placeholder="Type to Search"
            prepend-inner-icon="mdi-filter-variant"
            dense
          ></v-autocomplete>
        </template>
      </v-card>
    </v-dialog>
    <div class="mx-2 mt-3" v-if="searchBy === 'SEARCH_BY_CODE_OR_GRADE_NAME' && selectedCode">Code/Grade Name: 
      <v-chip
        color="primary"
        text-color="white"
      >
        {{selectedCode}}
      </v-chip>
    </div>
    <div class="mx-2 mt-3" v-if="searchBy === 'FILTER_BY_MANUFACTURER' && selectedManufacturer">Manufacturer: <v-chip
        color="primary"
        text-color="white"
      >
        {{selectedManufacturer}}
      </v-chip>
    </div>
    <div class="mx-2 mt-3" v-if="searchBy === 'FILTER_BY_VEHICLE' && selectedYear && selectedMake && selectedModel && selectedEngineSize">Vehicle: <v-chip
        color="primary"
        text-color="white"
      >
        {{selectedYear}} {{selectedMake}} {{selectedModel}} {{selectedEngineSize}}L
      </v-chip>
    </div>
  </v-container>

  <!-- desktop UI -->
  <div
    v-else
    class="d-flex justify-center align-center pt-6 pb-2 accent lighten-2"
  >
    <v-container>
      <v-row>
        <v-col cols="4">
          <v-select
            :items="allowedSearchByItems"
            :value="searchBy"
            @input="setSearchBy"
            dense
            solo
            background-color="primary darken-1"
            color="white"
            dark
          ></v-select>
        </v-col>
        <v-col
          v-if="searchBy === 'SEARCH_BY_CODE_OR_GRADE_NAME'"
          cols="8"
        >
          <v-combobox
            :value="selectedCode"
            @input="fetchByCode"
            :items="suggestionConverterCodes"
            :loading="isConvertersCodesLoading"
            :search-input.sync="searchCatalyticConvertersInput"
            solo
            :error-messages="suggestionConverterCodesError"
            hide-no-data
            hide-selected
            label="Select/Press Enter to Search"
            placeholder="Type to Search"
            dense
          ></v-combobox>
        </v-col>
        <v-col
          v-if="searchBy === 'FILTER_BY_MANUFACTURER'"
          cols="8"
        >
          <v-autocomplete
            :value="selectedManufacturer"
            @input="fetchByManufacturer"
            :items="allManufacturers"
            :loading="isManufacturersLoading"
            :disabled="isManufacturersLoading"
            solo
            :error-messages="manufacturersError"
            hide-no-data
            label="Select Manufacturer"
            placeholder="Type to Search"
            dense
          ></v-autocomplete>
        </v-col>
        <v-col
          v-if="searchBy === 'FILTER_BY_VEHICLE'"
          cols="2"
        >
          <v-autocomplete
            :value="selectedYear"
            @input="selectYear"
            :items="allYears"
            :loading="isYearsLoading"
            solo
            :error-messages="yearsError"
            hide-no-data
            label="Year"
            placeholder="Type to Search"
            dense
          ></v-autocomplete>
        </v-col>
        <v-col
          v-if="searchBy === 'FILTER_BY_VEHICLE' && selectedYear"
          cols="2"
        >
          <v-autocomplete
            :value="selectedMake"
            @input="selectMake"
            :items="makes"
            :loading="isMakesLoading"
            :disabled="isMakesLoading"
            solo
            :error-messages="makesError"
            hide-no-data
            label="Make"
            placeholder="Type to Search"
            dense
          ></v-autocomplete>
        </v-col>

        <v-col
          v-if="searchBy === 'FILTER_BY_VEHICLE' && selectedYear && selectedMake"
          cols="2"
        >
          <v-autocomplete
            :value="selectedModel"
            @input="selectModel"
            :items="models"
            :loading="isModelsLoading"
            :disabled="isModelsLoading"
            solo
            :error-messages="modelsError"
            hide-no-data
            label="Model"
            placeholder="Type to Search"
            dense
          ></v-autocomplete>
        </v-col>
        <v-col
          v-if="searchBy === 'FILTER_BY_VEHICLE' && selectedYear && selectedMake && selectedModel"
          cols="2"
        >
          <v-autocomplete
            :value="selectedEngineSize"
            @input="selectEngineSize"
            :items="engineSizes"
            :loading="isEngineSizesLoading"
            :disabled="isEngineSizesLoading"
            solo
            :error-messages="engineSizesError"
            hide-no-data
            label="Engine Size"
            placeholder="Type to Search"
            dense
          ></v-autocomplete>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { mapActions, mapState } from "@/store/modules/searchCatalyticConverters";
import { mapGetters } from "@/store/modules/user";
import { LOCAL_COLLECTOR } from "@/constants";
export default {
  data: () => ({
    searchDialogMobile: false,
    searchCatalyticConvertersInput: null,
  }),

  watch: {
    searchCatalyticConvertersInput(val) {
      val && val !== this.selectedCode && this.queryConverterCodes(val);
    },
  },

  computed: {
    ...mapState({
      suggestionConverterCodes: (state) => state.suggestionConverterCodes,
      isConvertersCodesLoading: (state) => state.isConvertersCodesLoading,
      suggestionConverterCodesError: (state) => state.suggestionConverterCodesError,
      selectedCode: (state) => state.selectedCode,
      allManufacturers: (state) => state.allManufacturers,
      selectedManufacturer: (state) => state.selectedManufacturer,
      isManufacturersLoading: (state) => state.isManufacturersLoading,
      manufacturersError: (state) => state.manufacturersError,
      allYears: (state) => state.allYears,
      selectedYear: (state) => state.selectedYear,
      isYearsLoading: (state) => state.isYearsLoading,
      yearsError: (state) => state.yearsError,
      makes: (state) => state.makes,
      selectedMake: (state) => state.selectedMake,
      isMakesLoading: (state) => state.isMakesLoading,
      makesError: (state) => state.makesError,
      models: (state) => state.models,
      selectedModel: (state) => state.selectedModel,
      isModelsLoading: (state) => state.isModelsLoading,
      modelsError: (state) => state.modelsError,
      engineSizes: (state) => state.engineSizes,
      selectedEngineSize: (state) => state.selectedEngineSize,
      isEngineSizesLoading: (state) => state.isEngineSizesLoading,
      engineSizesError: (state) => state.engineSizesError,
      searchBy: state => state.searchBy
    }),
    ...mapGetters(["role"]),
    allowedSearchByItems() {
      if (this.role === LOCAL_COLLECTOR) {
        return [
          {
            text: "Search by Code/Grade Name",
            value: "SEARCH_BY_CODE_OR_GRADE_NAME",
          },
          {
            text: "Filter by Manufacturer",
            value: "FILTER_BY_MANUFACTURER",
          },
        ];
      }
      return [
        {
          text: "Search by Code/Grade Name",
          value: "SEARCH_BY_CODE_OR_GRADE_NAME",
        },
        {
          text: "Filter by Manufacturer",
          value: "FILTER_BY_MANUFACTURER",
        },
        {
          text: "Filter by Vehicle",
          value: "FILTER_BY_VEHICLE",
        },
      ];
    },
  },

  methods: {
    ...mapActions({
      queryConverterCodes: "queryConverterCodes",
      setSearchBy: "setSearchBy",
      fetchByCode: "fetchByCode",
      fetchByManufacturer: "fetchByManufacturer",
      selectYear: "selectYear",
      selectMake: "selectMake",
      selectModel: "selectModel",
      selectEngineSize: "selectEngineSize",
    }),
    isAllowedSearchBy(searchById) {
      return this.allowedSearchByItems.some((el) => el.value === searchById);
    },
    searchByText(searchById) {
      const item = this.allowedSearchByItems.find((el) => el.value === searchById);
      return item.text;
    },
  },
};
</script>
